import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { DiamondHero3D } from "@/components/DiamondHero3D";
import { EditorialMarquee } from "@/components/EditorialMarquee";
import { TrustedByLogos } from "@/components/TrustedByLogos";
import { Reveal } from "@/components/Reveal";

const SECTIONS = [
  {
    to: "/about",
    num: "01",
    title: "The House",
    body: "Heritage, the JLT trading desk, and the three convictions we trade by.",
  },
  {
    to: "/capabilities",
    num: "02",
    title: "Capabilities",
    body: "Eight shapes, 0.30ct to 15ct+, certified sourcing and bespoke acquisition.",
  },
  {
    to: "/explorer",
    num: "03",
    title: "Market Explorer",
    body: "Configure a stone and read today's indicative wholesale index — before you call.",
  },
  {
    to: "/trust",
    num: "04",
    title: "Certifications & Trust",
    body: "GIA · IGI · HRD verified papers, insured logistics, bonded JLT vaulting.",
  },
  {
    to: "/gallery",
    num: "05",
    title: "The Gallery",
    body: "Whites, fancies and statement stones — under the light, side by side.",
  },
];

export const HomePage = ({ onRequestInventory }) => (
  <>
    <DiamondHero3D onRequestInventory={onRequestInventory} />
    <EditorialMarquee />
    <TrustedByLogos />

    <section className="mx-auto max-w-7xl px-6 py-24 lg:px-12 lg:py-32" data-testid="home-sections">
      <Reveal>
        <p className="eyebrow mb-4">The house, chapter by chapter</p>
        <h2 className="font-display max-w-2xl text-3xl font-light leading-snug tracking-tight text-white sm:text-4xl lg:text-5xl">
          Everything a buyer needs to <span className="gold-text italic">trust the desk</span>
        </h2>
      </Reveal>

      <div className="mt-16 space-y-0">
        {SECTIONS.map((s, i) => (
          <Reveal key={s.to} delay={i * 0.06}>
            <Link
              to={s.to}
              data-testid={`home-section-${s.num}`}
              className="group hairline flex items-center gap-6 border-t py-8 transition-colors duration-500 last:border-b hover:bg-white/[0.02] lg:gap-12 lg:py-10"
            >
              <span className="font-display w-12 shrink-0 text-sm italic tracking-[0.3em] text-[#C9A24B]">
                {s.num}
              </span>
              <div className="flex-1">
                <h3 className="font-display text-2xl font-light text-white transition-colors duration-300 group-hover:text-[#E8DCC0] sm:text-3xl">
                  {s.title}
                </h3>
                <p className="mt-1 text-sm text-[#717680]">{s.body}</p>
              </div>
              <ArrowRight className="h-5 w-5 shrink-0 text-[#717680] transition-all duration-300 group-hover:translate-x-2 group-hover:text-[#C9A24B]" />
            </Link>
          </Reveal>
        ))}
      </div>
    </section>

    <section className="mx-auto max-w-7xl px-6 pb-28 lg:px-12" data-testid="home-cta-band">
      <Reveal>
        <div className="glass-panel relative overflow-hidden rounded-3xl p-10 text-center lg:p-20">
          <div className="hero-bloom absolute inset-0" />
          <div className="relative">
            <p className="eyebrow mb-5">Trade only</p>
            <h2 className="font-display mx-auto max-w-2xl text-3xl font-light leading-snug text-white sm:text-4xl lg:text-5xl">
              Today's list moves fast. <span className="gold-text italic">Ask for it first.</span>
            </h2>
            <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-[#B8BCC2]">
              Certified parcels and single stones, refreshed as the desk trades. Send one request —
              the current list lands in your inbox within one business day.
            </p>
            <div className="mt-9 flex flex-wrap items-center justify-center gap-4">
              <button data-testid="home-cta-request-btn" onClick={onRequestInventory} className="gold-btn">
                Request Inventory <ArrowRight className="h-4 w-4" />
              </button>
              <Link to="/contact" data-testid="home-cta-contact-btn" className="glass-btn">
                Contact the Desk
              </Link>
            </div>
          </div>
        </div>
      </Reveal>
    </section>
  </>
);
